import { Query, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { speechStrategyEndpoints as endpoints } from '@/api/endpoints';
import { callGet, callPatch } from '@/api/fetcher';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { SpeechStrategy } from '@/shared/types/speechStrategy';
import { RootState } from '@/shared/types/state';

export const speechStrategyQueryKey = (brainId: string) =>
  ['brains', brainId, 'speech-strategy'] as const;

/** Matches the cached strategy of every agent. */
export const isSpeechStrategyQuery = ({ queryKey }: Query) =>
  queryKey[0] === 'brains' && queryKey[2] === 'speech-strategy';

// The step sources come from the speech models catalog, so the editor needs both reads
export const selectCanReadSpeechStrategy = (state: RootState) =>
  getPermissions(state, 'speech_strategy', actions.READ) &&
  getPermissions(state, 'speech_models', actions.READ);

/** Brain-scoped speech strategy, read and replaced as one document like the model strategy. */
export const useSpeechStrategy = (brainId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accountId = useSelector(selectAccountId);
  const canRead = useSelector(selectCanReadSpeechStrategy);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'speech_strategy', actions.WRITE)
  );
  const queryKey = speechStrategyQueryKey(brainId);

  const {
    data,
    status: getStatus,
    refetch,
  } = useQuery<SpeechStrategy>({
    queryKey,
    enabled: !!accountId && canRead,
    queryFn: () => callGet(endpoints.strategy(brainId)),
  });

  const { mutateAsync: save, status: saveStatus } = useMutation<
    SpeechStrategy,
    Error,
    SpeechStrategy
  >({
    mutationFn: (strategy) => callPatch(endpoints.strategy(brainId), strategy),
    onSuccess: (persisted) => {
      queryClient.setQueryData(queryKey, persisted);
      toast.success(t('ai_agents.speech_strategy.saved'));
    },
    meta: { errorToast: true },
  });

  return { strategy: data, getStatus, refetch, save, saveStatus, canRead, canWrite };
};
